import { P2GLink } from '@parcel2go/core-ui';
import {
  BasketRequirementExplicit,
  BasketRequirementMultiple,
  QuoteRequirement,
  QuoteRequirementSection
} from '../types/basket-types';
import { CustomsKeys } from '../types/customs';
import { RequirementTypes } from '../types/enums';

/**
 * Parses a field string into model and field components
 * e.g., "origin.companyStatus" -> { model: "origin", field: "companyStatus" }
 */
const parseFieldModel = (fieldString: string, defaultModel: string): { model: string; field: string } => {
  if (fieldString.indexOf('.') > -1) {
    const [model, field] = fieldString.split('.');
    return { model, field };
  }
  return { model: defaultModel, field: fieldString };
};

/**
 * Evaluates a single field comparison based on compare operator and value
 * @returns true if the comparison condition is met
 */
const evaluateFieldComparison = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldValue: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  compareValue: any,
  compareOp: 'Eq' | 'NotEq' = 'Eq'
): boolean => {
  // Handle equality comparison
  if (compareOp === 'Eq') {
    // Special handling for null comparison - treat undefined and empty string as null
    if (compareValue === null) {
      return fieldValue === null || fieldValue === undefined || fieldValue === '';
    }
    // For array values, check if field is in the array
    if (Array.isArray(compareValue)) {
      return compareValue.includes(fieldValue);
    }
    // For single values, check exact equality
    return fieldValue === compareValue;
  }

  // Handle inequality comparison
  if (compareOp === 'NotEq') {
    // Special handling for null comparison
    if (compareValue === null) {
      return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
    }
    // For array values, check if field is NOT in the array
    if (Array.isArray(compareValue)) {
      return !compareValue.includes(fieldValue);
    }
    // For single values, check inequality
    return fieldValue !== compareValue;
  }

  return false;
};

export const MapRequirement = (
  x: QuoteRequirement,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  basketSectionData: { [key: string]: any },
  defaultModel: string
) => ({ ...x, required: IsRequirementRequired(x, basketSectionData, defaultModel) });

export const IsRequirementRequired = (
  x: QuoteRequirement,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  basketSectionData: { [key: string]: any },
  defaultModel: string
): boolean => {
  const keys = Object.keys(x?.metadata?.requiredIf || {});

  if (keys.length === 0) {
    return x.required;
  }

  let newRequired = false;

  if (keys.includes('all')) {
    const req = x.metadata.requiredIf as BasketRequirementMultiple;
    newRequired =
      req.all?.every((e) => {
        const model = parseFieldModel(e.field, defaultModel);
        const fieldValue = basketSectionData[model.model]?.[model.field];
        const compareOp = e.compare || 'Eq';

        // Check if a value was explicitly provided
        const hasExplicitValue = e.value !== undefined || req.value !== undefined;
        if (!hasExplicitValue) {
          // No value specified - treat as empty array comparison (always false)
          return false;
        }

        const compareValue = e.value !== undefined ? e.value : req.value;
        return evaluateFieldComparison(fieldValue, compareValue, compareOp);
      }) || newRequired;
  } else if (keys.includes('any')) {
    const req = x.metadata.requiredIf as BasketRequirementMultiple;
    newRequired =
      req.any?.some((e) => {
        const model = parseFieldModel(e.field, defaultModel);
        const fieldValue = basketSectionData[model.model]?.[model.field];
        const compareOp = e.compare || 'Eq';

        // Check if a value was explicitly provided
        const hasExplicitValue = e.value !== undefined || req.value !== undefined;
        if (!hasExplicitValue) {
          // No value specified - treat as empty array comparison (always false)
          return false;
        }

        const compareValue = e.value !== undefined ? e.value : req.value;
        return evaluateFieldComparison(fieldValue, compareValue, compareOp);
      }) || newRequired;
  } else {
    const req = x.metadata.requiredIf as BasketRequirementExplicit;
    const model = parseFieldModel(req.field, defaultModel);
    const fieldValue = basketSectionData[model.model]?.[model.field];
    const compareOp = req.compare || 'Eq';
    const compareValue = req.value;

    newRequired = evaluateFieldComparison(fieldValue, compareValue, compareOp);
  }

  return !!(x.required || newRequired);
};

export const SwapRequirementsFromSelectedExtras = (
  type: RequirementTypes,
  additionalRequirements: QuoteRequirement[],
  masterRequirementSection: QuoteRequirementSection
) => {
  // Swap out some requirements based on selected extras. Eg Phone ->  Mobile when SMS is selected.
  additionalRequirements?.forEach((x) => {
    const r = {
      requirementType: (x?.key.split('.')[0] || '') as RequirementTypes,
      requirementKey: x?.key.split('.')[1] || ''
    };

    // Just look at the requirements for the current requirementType
    if (type === r.requirementType && r.requirementKey && r.requirementType) {
      if (masterRequirementSection?.rules?.some((x) => x.key === r.requirementKey)) {
        // If exists, find and replace certain fields. Don't change the key or the full model.
        masterRequirementSection.rules.forEach((rule) => {
          if (rule.key === r.requirementKey) {
            rule.required = x.required;
            rule.name = x.name;
            rule.metadata = { ...rule.metadata, ...(x.metadata || {}) };
          }
        });
      } else {
        // Add new requirement
        masterRequirementSection.rules?.push(x);
      }
    }
  });
};

export const AddRuleExamples = (
  masterRequirementSection: QuoteRequirementSection,
  type: RequirementTypes.ORIGIN | RequirementTypes.DESTINATION,
  country: string
) => {
  masterRequirementSection?.rules
    ?.filter((rule) => rule.metadata && !rule.metadata['example'])
    .forEach((rule) => {
      switch (rule.key) {
        case CustomsKeys.Address.EORI:
          if (type === RequirementTypes.ORIGIN && country.startsWith('GBR')) {
            rule.metadata['example'] = 'EORI number must be GB followed by 12 numbers';
            rule.metadata.helperText = (
              <>
                Don't have a EORI number? Apply for one{' '}
                <P2GLink href="https://www.gov.uk/eori" target="_blank" dataTestId="apply-uk-eori-link">
                  https://www.gov.uk/eori
                </P2GLink>
              </>
            );
          } else {
            rule.metadata['example'] = 'EORI number must be a country code followed by at least 7 numbers';
          }
          break;
      }
    });
};
